import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Bell, ChevronLeft, CheckCheck, Sparkles } from "lucide-react";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";

/**
 * PlyxInbox — persisted notification feed.
 *
 * Backs the WebSocket leaderboard ripple with durable history so users
 * who were offline at the moment a follow's video crossed 100 likes
 * still get a tap-able "go watch" entry the next time they open the
 * app. Reads from `GET /api/plyx/me/notifications`; bulk-clears the
 * unread badge on first paint via `POST /plyx/me/notifications/read-all`.
 *
 * Layout follows the rest of the Plyx surfaces:
 *   • Dark canvas with a single back-affordance top-left.
 *   • Each card is tap-anywhere — the entire row deep-links to the
 *     creator's clip.
 *   • Empty state celebrates the quiet ("All caught up") rather than
 *     scolding the user for not having followers yet.
 */

const formatRelative = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

const ICONS = {
  "ripple.likes": Sparkles,
};

const PlyxInbox = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await plyxApi.get("/plyx/me/notifications?limit=50");
      setItems(r.data?.notifications || []);
      setUnread(r.data?.unread_count || 0);
    } catch {
      // Surface nothing — the empty state is the same shape as a
      // network blip, and a toast on every cold-open would be noise.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-clear the unread badge once the user has actually opened the
  // inbox. Fire-and-forget; if it fails the next /badge poll will
  // surface the correct count.
  useEffect(() => {
    if (!user || loading || unread === 0) return;
    plyxApi.post("/plyx/me/notifications/read-all").catch(() => {});
  }, [user, loading, unread]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/plyx/login" replace state={{ from: "/plyx/inbox" }} />;

  return (
    <div
      className="min-h-[100dvh] bg-[#0A0A0A] text-white pb-24"
      data-testid="plyx-inbox-page"
    >
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/85 backdrop-blur px-4 py-3 border-b border-white/5 flex items-center gap-3">
        <Link
          to="/plyx"
          className="p-2 -ml-2 rounded-full hover:bg-white/5 transition"
          data-testid="plyx-inbox-back"
          aria-label="Back to feed"
        >
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-300" />
            Inbox
          </h1>
          <p className="text-[11px] text-white/45 -mt-0.5">
            Wins, milestones, and waves from people you follow.
          </p>
        </div>
        {unread > 0 && (
          <span
            className="text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-300 text-black"
            data-testid="plyx-inbox-unread-badge"
          >
            {unread}
          </span>
        )}
      </header>

      {loading ? (
        <ul className="px-4 mt-4 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <li
              key={i}
              className="h-16 rounded-2xl bg-white/[0.04] animate-pulse"
            />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div
          className="px-6 pt-24 flex flex-col items-center text-center"
          data-testid="plyx-inbox-empty"
        >
          <div className="text-5xl mb-3" aria-hidden>✨</div>
          <h2 className="text-base font-semibold">All caught up</h2>
          <p className="text-sm text-white/55 max-w-xs mt-1">
            When creators you follow cross a like milestone, you&apos;ll
            see the ripple here — even if you missed the live burst.
          </p>
          <Link
            to="/plyx/discover"
            className="mt-5 inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full bg-amber-300 text-black hover:bg-amber-200 transition"
            data-testid="plyx-inbox-discover-cta"
          >
            Discover creators
          </Link>
        </div>
      ) : (
        <ul className="px-3 mt-3 space-y-2">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <li key={n.notification_id}>
                <Link
                  to={n.link || "/plyx"}
                  data-testid={`plyx-inbox-item-${n.notification_id}`}
                  className={
                    "block rounded-2xl border px-4 py-3 transition " +
                    (n.is_read
                      ? "bg-white/[0.025] border-white/5"
                      : "bg-amber-300/[0.06] border-amber-300/25")
                  }
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={
                        "shrink-0 w-9 h-9 rounded-full flex items-center justify-center " +
                        (n.is_read ? "bg-white/5" : "bg-amber-300 text-black")
                      }
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <p className="text-sm font-semibold leading-snug truncate">
                          {n.title}
                        </p>
                        <span className="ml-auto text-[11px] text-white/40 whitespace-nowrap">
                          {formatRelative(n.created_at)}
                        </span>
                      </div>
                      {n.body ? (
                        <p className="text-xs text-white/55 mt-1 line-clamp-2">
                          {n.body}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {items.length > 0 && unread > 0 ? (
        <div className="px-4 mt-4 flex justify-center">
          <span className="text-[11px] text-white/40 inline-flex items-center gap-1.5">
            <CheckCheck className="w-3.5 h-3.5 text-emerald-300" />
            Marked as read
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default PlyxInbox;
